import React, { useCallback, useState } from 'react';
import { ButtonProps } from '../../../../../components/menus/buttons/types';
import EditorLink from '@wix/da-ds/pkg/Icons/24x24/EditorLink';
import { useTranslation } from 'react-i18next';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import { OffsetModifier } from '@wix/da-ds/pkg/popper/types';
import EditorIconButton from '../../../../../components/menus/_partials/EditorIconButton';
import { TOOLTIP_HOVER_DELAY } from '../../../../../constants';
import cn from 'classnames';
import TextInput from '@wix/da-ds/pkg/formControls/lego/InputField';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { DaDeviationAttrs } from '../../../deviation';
import {
  addMissingProtocol,
  validateHref,
} from '../../../../../components/utils/validateHref';
import { getCurrentUser } from '../../../../../components/utils/tiptap';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import Close from '@wix/da-ds/pkg/Icons/24x24/Close';
import { isTouch } from '../../../../../components/utils/browser';

import s from './Link.scss';

const offsetModifier: OffsetModifier = [0, 6];

const Link: React.FC<ButtonProps> = ({ editor }) => {
  const attrs: DaDeviationAttrs = editor.getAttributes(
    'da-deviation'
  ) as DaDeviationAttrs;

  const { t } = useTranslation('editor');
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState(attrs.link ?? '');
  const [valid, setValid] = useState(true);
  const currentUser = getCurrentUser(editor);
  const allowLink = attrs?.deviation?.author?.userId === currentUser?.userId;

  const handleSave = useCallback(
    e => {
      if (validateHref(addMissingProtocol(href)) || href === '') {
        editor.commands.updateAttributes('da-deviation', {
          link: href === '' ? null : addMissingProtocol(href),
        });
        setOpen(false);
      }
    },
    [editor.commands, href]
  );

  const handleChange = useCallback(e => {
    setHref(e.target.value.trim());
    setValid(validateHref(addMissingProtocol(e.target.value.trim())));
  }, []);

  return (
    <PositionOnlyPopperPopover
      popoverContent={
        <div className={cn(s['root'], 'ds-surface-tertiary')}>
          <TextInput
            id="link"
            value={href}
            onChange={handleChange}
            validationResult={!valid ? 'error' : 'success'}
            placeholder={t('ext.deviation.settings.link.noanchor.placeholder')}
            postfix={
              <IconButton
                icon={Close}
                aria-label={t('common.close')}
                size="small"
                onClick={() => {
                  setHref('');
                  setValid(true);
                }}
              />
            }
            onFocus={e => {
              setTimeout(() => {
                isTouch() && e.target.scrollIntoView();
              }, 300);
            }}
          />
          <BrandedButton
            onClick={handleSave}
            size="medium"
            variant="brand-green"
          >
            {t('editor.deviation.link.save')}
          </BrandedButton>
        </div>
      }
      placement="bottom"
      isVisible={open}
      offsetModifier={offsetModifier}
      onClickOutside={() => {
        setOpen(false);
      }}
    >
      <EditorIconButton
        featureId="node:deviation:link"
        disabled={!allowLink}
        icon={EditorLink}
        onClick={() => {
          setOpen(!open);
        }}
        tooltipHoverDelay={TOOLTIP_HOVER_DELAY}
        tooltipText={t('ext.deviation.settings.link.label')}
        aria-label={t('ext.deviation.settings.link.label')}
      />
    </PositionOnlyPopperPopover>
  );
};
Link.displayName = 'Link';

export default Link;
